import { useState, useMemo } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);

  const missingToken = !token;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password,
      });
      setDone(true);
      toast.success('Password updated. You can now sign in.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not reset password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="min-h-[80vh] flex items-center justify-center px-4 py-24 bg-[#0A192F]" data-testid="reset-password-page">
      <div className="w-full max-w-md glass-heavy border border-white/10 p-8 sm:p-10 text-white">
        <p className="text-[11px] tracking-[0.3em] uppercase text-gold mb-2">Account security</p>
        <h1 className="text-3xl mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          {done ? 'Password updated' : 'Reset your password'}
        </h1>

        {missingToken && !done && (
          <div className="mt-6 space-y-4" data-testid="reset-missing-token">
            <p className="text-sm text-white/70 leading-relaxed">
              This reset link is missing its token. Please use the latest link from your email,
              or request a new one from the sign-in screen.
            </p>
            <Link
              to="/"
              className="inline-block text-xs uppercase tracking-[0.2em] text-gold hover:text-[#E5C55D] transition-colors"
            >
              ← Back to home
            </Link>
          </div>
        )}

        {!missingToken && !done && (
          <form onSubmit={handleSubmit} className="mt-6 space-y-5" data-testid="reset-password-form">
            <p className="text-sm text-white/70 leading-relaxed">
              Choose a new password for your account. The link expires 60 minutes after it was sent
              and can only be used once.
            </p>
            <div>
              <Label htmlFor="new-password" className="text-white/80">New password</Label>
              <Input
                id="new-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-transparent border-b border-white/20 rounded-none px-0 focus:border-gold"
                minLength={8}
                required
                data-testid="reset-new-password-input"
              />
              <p className="mt-1 text-[11px] text-white/40">Minimum 8 characters.</p>
            </div>
            <div>
              <Label htmlFor="confirm-password" className="text-white/80">Confirm password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="bg-transparent border-b border-white/20 rounded-none px-0 focus:border-gold"
                minLength={8}
                required
                data-testid="reset-confirm-password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-gold text-[#0A192F] hover:bg-[#E5C55D]"
              disabled={isLoading}
              data-testid="reset-submit-btn"
            >
              {isLoading ? 'Updating...' : 'Update password'}
            </Button>
          </form>
        )}

        {done && (
          <div className="mt-6 space-y-5" data-testid="reset-success">
            <p className="text-sm text-white/70 leading-relaxed">
              Your password has been updated. For your security, all active sessions have been signed out.
              You can now sign in with your new password.
            </p>
            <Button
              type="button"
              onClick={() => navigate('/')}
              className="w-full bg-gold text-[#0A192F] hover:bg-[#E5C55D]"
              data-testid="reset-go-home-btn"
            >
              Continue to sign in
            </Button>
          </div>
        )}
      </div>
    </main>
  );
}
